import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  AcademicCapIcon,
  ArrowRightIcon,
  PlayIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, Badge } from '@/components/ui'
import { lmsApi } from '@/api'
import type { DashboardCourse } from '@/api/dashboard'

interface Props {
  courses?: DashboardCourse[]
  completedCount?: number
}

export default function CourseProgressWidget({ courses: initialCourses, completedCount = 0 }: Props) {
  const [courses, setCourses] = useState<DashboardCourse[]>(initialCourses || [])
  const [loading, setLoading] = useState(!initialCourses)

  useEffect(() => {
    if (!initialCourses) {
      fetchCourses()
    }
  }, [initialCourses])

  const fetchCourses = async () => {
    try {
      const enrollments = await lmsApi.getMyEnrollments()
      const inProgress = enrollments
        .filter((e) => !e.completed_at)
        .slice(0, 4)
        .map((e) => ({
          id: e.course.id,
          title: e.course.title,
          slug: e.course.slug,
          progress: e.progress || 0
        }))
      setCourses(inProgress)
    } catch (error) {
      console.error('Error fetching courses:', error)
    } finally {
      setLoading(false)
    }
  }

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'bg-green-500'
    if (progress >= 50) return 'bg-blue-500'
    if (progress >= 25) return 'bg-yellow-500'
    return 'bg-gray-400'
  }

  const getProgressBadge = (progress: number) => {
    if (progress >= 80) return { text: 'Quasi completo', variant: 'success' as const }
    if (progress >= 50) return { text: 'A buon punto', variant: 'info' as const }
    if (progress >= 25) return { text: 'In corso', variant: 'warning' as const }
    return { text: 'Appena iniziato', variant: 'default' as const }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader title="I miei corsi" />
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </Card>
    )
  }

  return (
    <Card className="h-full">
      <CardHeader
        title={
          <div className="flex items-center gap-2">
            <AcademicCapIcon className="h-5 w-5 text-blue-500" />
            <span>I miei corsi</span>
          </div>
        }
        action={
          <Link
            to="/courses/my"
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
          >
            Tutti <ArrowRightIcon className="h-4 w-4 ml-1" />
          </Link>
        }
      />

      {/* Stats summary */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
        <div className="flex items-center gap-2">
          <PlayIcon className="h-5 w-5 text-blue-600" />
          <div>
            <p className="text-lg font-bold text-blue-900">{courses.length}</p>
            <p className="text-xs text-blue-600">In corso</p>
          </div>
        </div>
        <div className="h-8 w-px bg-blue-200" />
        <div className="flex items-center gap-2">
          <CheckCircleIcon className="h-5 w-5 text-green-600" />
          <div>
            <p className="text-lg font-bold text-green-900">{completedCount}</p>
            <p className="text-xs text-green-600">Completati</p>
          </div>
        </div>
      </div>

      {courses.length > 0 ? (
        <div className="space-y-3">
          {courses.map((course) => {
            const progressInfo = getProgressBadge(course.progress)

            return (
              <Link
                key={course.id}
                to={`/courses/${course.slug}`}
                className="block p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {course.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge size="sm" variant={progressInfo.variant}>
                        {progressInfo.text}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <span className="text-lg font-bold text-gray-900">
                      {course.progress}%
                    </span>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-3">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getProgressColor(course.progress)} rounded-full transition-all duration-500`}
                      style={{ width: `${course.progress}%` }}
                    />
                  </div>
                </div>

                {/* Continue button */}
                <div className="mt-2 flex justify-end">
                  <span className="text-xs text-primary-600 flex items-center gap-1 hover:text-primary-700">
                    Continua <ArrowRightIcon className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <AcademicCapIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-gray-500 mb-3">
            Non sei iscritto a nessun corso
          </p>
          <Link
            to="/courses"
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
          >
            Scopri i corsi <ArrowRightIcon className="h-4 w-4 ml-1" />
          </Link>
        </div>
      )}
    </Card>
  )
}
